import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import BasicList from 'core/modules/components/lists/components/BasicList/BasicList';
import CalendarItem from 'core/modules/components/lists/calendars/CalendarItem/CalendarItem';
import { isCourseReadOnly, isSchoolProgram } from 'core/utils/course';
import React from 'react';
import Icon from '../../../atoms/Icon/Icon';

/**
 * CalendarsList - Lista de calendarios de cursos. Envuelve el genérico v2 `BasicList`
 * (semántica `<ul>/<li>` accesible) y, opcionalmente, agrupa por colegio con acordeones.
 */

// `CalendarItem` es JS (props sin tipar): se aliasa a un componente permisivo para poder
// inyectar las props de dominio de cada calendario mediante spread.
const CalendarRow = CalendarItem as unknown as React.FC<Record<string, unknown>>;

export type Calendar = Readonly<{
  guid?: string;
  theme_color?: string;
  theme_image?: string;
  course_guid?: string;
}> & Record<string, unknown>;

export type CalendarGroup = Readonly<{
  guid: string;
  name: string;
  calendars: Calendar[];
}>;

type CalendarsListProps = Readonly<{
  calendars?: Calendar[];
  calendarsShows?: string[];
  configProgramPicture?: boolean;
  onClickVisible?: (...args: unknown[]) => void;
  onClickVisibleGroup?: (calendars: Calendar[], event: React.MouseEvent) => void;
  onConfigureCourse?: (calendar: Calendar) => void;
  schoolGroupsCalendars?: CalendarGroup[] | null;
  showOptions?: boolean;
}>;

function CalendarsList({
  calendars = [],
  calendarsShows = [],
  configProgramPicture = false,
  onClickVisible = () => {},
  onClickVisibleGroup = () => {},
  onConfigureCourse = () => {},
  schoolGroupsCalendars = null,
  showOptions = true,
}: CalendarsListProps): JSX.Element {
  if (schoolGroupsCalendars?.length) {
    return (
      <div className="calendars-list">
        {schoolGroupsCalendars.map((group) => (
          <Accordion key={group.guid} className="calendars-list__group" defaultExpanded={true}>
            <AccordionSummary expandIcon={<Icon name="chevron-down" />} aria-controls={`${group.guid}-content`} id={`${group.guid}-header`}>
              <div className="calendars-list__group-summary">
                <div className="calendars-list__group-name">{group.name}</div>
                <div className="calendars-list__group-options-wrapper">
                  <div className="calendar-item__visible-icon" onClick={(e) => onClickVisibleGroup(group.calendars, e)}>
                    <Icon size="md" name={group.calendars.every((calendar) => calendarsShows.includes(calendar.guid)) ? 'visible' : 'visible-off'} />
                  </div>
                </div>
              </div>
            </AccordionSummary>
            <AccordionDetails className="calendars-list__group-details">
              <BasicList
                items={group.calendars.map((item) => (
                  <CalendarRow
                    {...item}
                    key={item?.guid}
                    themeColor={item.theme_color}
                    themeImage={item.theme_image}
                    courseGuid={item.course_guid}
                    visible={calendarsShows.includes(item.guid)}
                    onClickVisible={onClickVisible}
                    showOptions={showOptions && !isSchoolProgram(item) && !isCourseReadOnly(item)}
                    onConfigureCourse={() => onConfigureCourse(item)}
                    configProgramPicture={configProgramPicture}
                  />
                ))}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    );
  }

  return (
    <div className="calendars-list">
      <BasicList
        items={calendars.map((item) => (
          <CalendarRow
            {...item}
            key={item?.guid}
            themeColor={item.theme_color}
            themeImage={item.theme_image}
            courseGuid={item.course_guid}
            visible={calendarsShows.includes(item.guid)}
            onClickVisible={onClickVisible}
            showOptions={showOptions && !isSchoolProgram(item) && !isCourseReadOnly(item)}
            onConfigureCourse={() => onConfigureCourse(item)}
            configProgramPicture={configProgramPicture}
            showGroupName={true}
          />
        ))}
      />
    </div>
  );
}

export default CalendarsList;
